import { BrowserTracing } from '@sentry/tracing'
import * as Sentry from '@sentry/vue'
import Vue from 'vue'
import VueRouter from 'vue-router'
import useJwt from '@/auth/jwt/useJwt'
import {
  getHomeRouteForLoggedInUser, getUserData,
} from '@/auth/utils'
// Routes
import usePermission from '../composable/usePermission'
import accounts from './routes/accounts'
import admin from './routes/admin'
import batches from './routes/batches'
import dashboard from './routes/dashboard'
import operations from './routes/operations'
import pages from './routes/pages'
import postings from './routes/postings'
import reports from './routes/reports'
import echannels from './routes/e-channels'
import fraudMonitor from './routes/fraud-monitor'
import lms from './routes/lms'
import FavoriteService from '../helpers/favoriteService'
import { storeOriginalUrl, getAndClearIntendedUrl, routeRequiresAuth } from '../helpers/misc'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      redirect: { name: 'auth-login' },
    },
    ...accounts,
    ...admin,
    ...dashboard,
    ...operations,
    ...pages,
    ...postings,
    ...reports,
    ...batches,
    ...echannels,
    ...fraudMonitor,
    ...lms,
    {
      path: '/*',
      redirect: 'error-404',
    },
  ],
})

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    Vue,
    environment: process.env.BASE_URL,
    dsn: 'https://a3ac9acb56874e0190d3502f7c0d5da6@o947379.ingest.sentry.io/5896758',
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [process.env.BASE_URL, 'udara360.web', /^\//],
        tracePropagationTargets: [process.env.BASE_URL, 'udara360.web', /^\//],
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

router.beforeEach((to, from, next) => {
  const token = useJwt.getToken()
  const refreshToken = useJwt.getRefreshToken()
  const isLoggedIn = token && refreshToken
  const { getUserPermission } = usePermission()

  // Handle login with redirect query parameter
  if (to.path === '/login' && to.query.redirect) {
    const redirectPath = to.query.redirect
    storeOriginalUrl(redirectPath)
    return next({ name: 'auth-login' })
  }

  // Dynamic page title
  window.document.title = to.meta && to.meta.pageTitle ? to.meta.pageTitle : 'Udara360'

  const requiresAuth = routeRequiresAuth(to)

  if (requiresAuth) {
    // Store the original URL for redirection after login
    if (!isLoggedIn && to.name !== 'auth-login') {
      storeOriginalUrl(to.fullPath)
      return next({ name: 'auth-login' })
    }

    if (to.meta.redirectIfLoggedIn && isLoggedIn) {
      const userData = getUserData()
      return next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
    }

    if (!useJwt.checkTokenExpiration()) {
      if (!useJwt.checkRefreshTokenExpiration()) {
        // Store the URL before redirecting to login
        storeOriginalUrl(to.fullPath)
        return next({ name: 'auth-login' })
      }
      useJwt.refreshToken().then(r => {
        useJwt.isAlreadyFetchingAccessToken = false

        // Update accessToken in localStorage
        useJwt.setToken(r.data.accessToken)
        useJwt.setRefreshToken(r.data.refreshToken)

        useJwt.onAccessTokenFetched(r.data.accessToken)
      })
      return next()
    } if (to.meta.hasPermission) {
      if (getUserPermission(to.meta.hasPermission)) {
        return next()
      }
      return next({ name: 'misc-not-authorized' })
    }
    return next()
  }
  return next()
})

export const redirectAfterLogin = () => {
  const intendedUrl = getAndClearIntendedUrl()
  if (intendedUrl) {
    // Check if the intended URL is a valid route
    const matchedRoute = router.resolve(intendedUrl).route
    if (matchedRoute && matchedRoute.name !== '404') {
      return router.push(intendedUrl)
    }
  }

  // Default route if no intended URL or URL is invalid
  // const userData = getUserData()
  return router.push({ name: 'home' })
}

// ? For splash screen
router.afterEach(to => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }

  // Track favourite logic
  if (to.name && to.meta && !to.meta.skipFavorites) {
    const currentRoute = {
      name: to.meta.pageTitle || to.name,
      route: to.name,
      icon: to.meta.icon || 'StarIcon',
    }

    FavoriteService.recordInteraction(currentRoute)
  }
})

export default router
